// 異地備份頁的純函式（plan/vms-web-v0.md §5.12）：容量 / 碼率顯示、故障域、覆蓋率摘要、表單檢查。
import type { BackupKind, BackupTargetIn, ChannelCoverage, FaultDomain } from './planeApi';

/** 十進位單位（容量估算照備援規格 §8 的十進位 GB）。 */
export function fmtBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let v = n;
  while (v >= 1000 && i < u.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

export function fmtBps(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)} Mbps`;
  return `${Math.round(n / 1e3)} kbps`;
}

/** GB（十進位）字串 → bytes；空或非數字回 0。 */
export function gbToBytes(s: string): number {
  const v = Number(s);
  return Number.isFinite(v) && v > 0 ? Math.round(v * 1e9) : 0;
}

export function faultComplete(f: FaultDomain | undefined): boolean {
  return !!f && !!f.site?.trim() && !!f.facility?.trim() && !!f.power?.trim() && !!f.network?.trim();
}

const RANK: Record<string, number> = { ok: 0, not_recording: 0, unknown: 1, partial: 2, degraded: 3 };

/** 政策的覆蓋率摘要：最差的狀態、最落後的通道、待傳量、缺口數。 */
export function coverageSummary(cov: ChannelCoverage[] | undefined): {
  worst: string;
  maxLag: number;
  pendingBytes: number;
  missing: number;
  oldestProtected?: string;
} {
  let worst = 'ok';
  let maxLag = 0;
  let pendingBytes = 0;
  let missing = 0;
  let oldest: string | undefined;
  for (const c of cov ?? []) {
    if ((RANK[c.state] ?? 1) > (RANK[worst] ?? 0)) worst = c.state;
    maxLag = Math.max(maxLag, c.lag_seconds || 0);
    pendingBytes += c.pending_bytes || 0;
    missing += c.missing_ranges?.length ?? 0;
    if (c.protected_until && (!oldest || c.protected_until < oldest)) oldest = c.protected_until;
  }
  if (!cov || cov.length === 0) worst = 'unknown';
  return { worst, maxLag, pendingBytes, missing, oldestProtected: oldest };
}

/** 秒數 → 「2 分 5 秒」的簡短表示（落後 / 待傳時間）。 */
export function fmtLag(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '0s';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m`;
}

/** 登記目的地的表單檢查（伺服器還會再驗一次）；回 i18n key 或 null。 */
export function targetFormError(t: BackupTargetIn, creating: boolean): string | null {
  if (!t.name.trim()) return 'backup.v_name';
  if (!(t.quota_bytes > 0)) return 'backup.v_quota';
  switch (t.kind as BackupKind) {
    case 'nas_s3':
      if (!/^https?:\/\/[^/\s]+\/?$/.test(t.endpoint ?? '')) return 'backup.v_endpoint_s3';
      if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(t.bucket ?? '')) return 'backup.v_bucket';
      if (creating && (!t.credentials?.write?.access_key_id || !t.credentials?.write?.secret_access_key)) return 'backup.v_creds';
      if (t.access_path === 'via_device' && !t.proxy_ref) return 'backup.v_proxy';
      return null;
    case 'nas_smb':
      if (!/^\/\/[A-Za-z0-9.-]+(:\d+)?\/[^/\\]+/.test(t.endpoint ?? '')) return 'backup.v_endpoint_smb';
      if (creating && (!t.credentials?.write?.username || !t.credentials?.write?.password)) return 'backup.v_creds';
      if (!t.proxy_ref) return 'backup.v_proxy';
      return null;
    case 'nas_nfs':
      if (!/^[A-Za-z0-9.-]+:\/\S*$/.test(t.endpoint ?? '')) return 'backup.v_endpoint_nfs';
      if (!t.proxy_ref) return 'backup.v_proxy';
      return null;
    case 'nvr':
      if (!t.nvr_ref) return 'backup.v_nvr';
      return null;
  }
  return 'backup.v_kind';
}
